import {
	ToothRestoration,
	ToothRestorationFilling,
	ToothRestorationFillingValues,
	ToothSurface,
} from '../../../../../graphql/graphql';
import { useCallback, useMemo } from 'react';
import { SurfaceOperation } from '../../../Enums/SurfaceOperation.enum';
import { handleSurfacesOperation } from '../../../Helpers/surfaces/toothSurfaces.helper';

interface Props {
	restoration?: ToothRestoration;
	setRestoration: (restoration: ToothRestoration) => void;
}

export default function useToothRestorationFillings({
	restoration,
	setRestoration,
}: Props) {
	const getSurfaces = useCallback(
		(index: number) => restoration?.fillings?.[index]?.surfaces ?? [],
		[restoration, setRestoration],
	);

	const setSurfaces = useCallback(
		(index: number, surfaces: ToothSurface[], operation: SurfaceOperation) => {
			const nextFillings = [...(restoration?.fillings ?? [])];
			const currentFilling = nextFillings[index] ?? {};

			nextFillings[index] = {
				...currentFilling,
				surfaces: handleSurfacesOperation(
					currentFilling?.surfaces ?? [],
					surfaces,
					operation,
				),
			};

			setRestoration({
				...restoration,
				fillings: nextFillings,
			});
		},
		[restoration, setRestoration],
	);

	const getValues = useCallback(
		(index: number) => restoration?.fillings?.[index]?.values ?? {},
		[restoration, setRestoration],
	);

	const setValues = useCallback(
		(index: number, values: ToothRestorationFillingValues) => {
			const nextFillings = [...(restoration?.fillings ?? [])];
			const currentFilling = nextFillings[index] ?? {};

			nextFillings[index] = {
				...currentFilling,
				surfaces: currentFilling?.surfaces ?? [],
				values,
			};

			setRestoration({
				...restoration,
				fillings: nextFillings,
			});
		},
		[restoration, setRestoration],
	);

	const reset = useCallback(
		(index: number) => {
			const nextFillings = [...(restoration?.fillings ?? [])];

			nextFillings.splice(index, 1);

			setRestoration({
				...restoration,
				fillings: nextFillings,
			});
		},
		[restoration, setRestoration],
	);

	return useMemo(
		() => ({
			fillings: {
				getValues,
				setValues,
				getSurfaces,
				setSurfaces,
				reset,
			},
		}),
		[restoration],
	);
}
